/**
 * Semáforo de estados (balizas): nombre, color y orden de cada estado que se ve sobre los autos y en
 * la leyenda, y qué etapas agrupa cada uno (prompt §7: «Balizas de color, definidas por cada
 * negocio»). Es la única fuente: la escena 3D, la leyenda del monitor y la escena de referencia leen
 * de aquí (corrección del 2026-10-14; antes, la leyenda tenía su propia lista).
 *
 * Cada espacio puede tener su propia definición (paso 1.2). Si no la tiene, se usa la definición por
 * defecto, armada con los 7 estados acordados con Renato (CLAUDE.md, «Balizas por defecto») y las
 * etapas del proceso del espacio.
 */

import type { DefinicionDeProceso, Etapa } from './proceso';

/** Ids de los 7 estados por defecto. Un negocio puede agregar estados con otros ids. */
export type IdEstadoBaliza =
  'recepcionado' | 'en-cola' | 'en-reparacion' | 'espera' | 'control-lavado' | 'listo' | 'atrasado';

/**
 * Reglas que tienen prioridad sobre las etapas: «atraso» (la OT superó su hora prometida y aún no
 * está lista) y «listo» (el auto llegó al hito «listo»). Atraso gana a listo y listo a las etapas.
 */
export type ReglaDeBaliza = 'atraso' | 'listo';

export interface EstadoDeBaliza {
  id: string;
  nombre: string;
  /** Color saturado en `#rrggbb`. */
  color: string;
  /** Parpadea entre su color y apagado (por ejemplo, «Atrasado»). */
  parpadea: boolean;
  regla?: ReglaDeBaliza;
}

/**
 * Momento de un auto dentro de una etapa: esperando o trasladándose hacia ella, en curso, o con la
 * actividad interrumpida (por ejemplo, esperando un repuesto en el elevador).
 */
export type FaseDeEtapa = 'espera' | 'curso' | 'interrumpida';

export const FASES: readonly FaseDeEtapa[] = ['espera', 'curso', 'interrumpida'];

export interface DefinicionDeSemaforo {
  /** Con `false`, las balizas quedan apagadas y no hay leyenda. */
  activo: boolean;
  /** En el orden de la leyenda. */
  estados: EstadoDeBaliza[];
  /** Estado de cada fase de cada etapa (id de estado, o `null` = sin baliza). */
  asignacion: Record<string, Partial<Record<FaseDeEtapa, string | null>>>;
}

/** Los 7 estados por defecto, en el orden de la leyenda. */
export const SEMAFORO_POR_DEFECTO: readonly EstadoDeBaliza[] = [
  { id: 'recepcionado', nombre: 'Recepcionado', color: '#ffffff', parpadea: false },
  { id: 'en-cola', nombre: 'En cola', color: '#1565ff', parpadea: false },
  { id: 'en-reparacion', nombre: 'En reparación', color: '#ffc400', parpadea: false },
  { id: 'espera', nombre: 'Espera de repuesto o aprobación', color: '#a020f0', parpadea: false },
  { id: 'control-lavado', nombre: 'Control o lavado', color: '#00c8ff', parpadea: false },
  { id: 'listo', nombre: 'Listo', color: '#00c853', parpadea: false, regla: 'listo' },
  { id: 'atrasado', nombre: 'Atrasado', color: '#ff1a1a', parpadea: true, regla: 'atraso' },
];

/** Colores saturados para elegir (los del semáforo por defecto y algunos más). */
export const COLORES_DE_BALIZA: readonly string[] = [
  '#ffffff',
  '#1565ff',
  '#00c8ff',
  '#00c853',
  '#7cd31f',
  '#ffc400',
  '#ff8a00',
  '#ff1a1a',
  '#a020f0',
  '#ff3fa4',
];

/** Color de una baliza apagada (auto aún sin recepcionar o medio período de un parpadeo). */
export const COLOR_BALIZA_APAGADA = '#3a3d42';

/** Medio período del parpadeo (ms): encendido y apagado. */
export const PARPADEO_MS = 450;

/** `#rrggbb` → entero 0xrrggbb. */
export function colorANumero(color: string): number {
  return Number.parseInt(color.slice(1), 16);
}

export function esColorValido(color: string): boolean {
  return /^#[0-9a-fA-F]{6}$/.test(color);
}

/**
 * Estado por defecto de cada fase de una etapa, según dónde ocurre y qué es (no según su nombre):
 * así sirve también para etapas que agrega un negocio.
 */
export function asignacionPorDefecto(
  e: Etapa,
  lugares: ReadonlySet<string>,
): Partial<Record<FaseDeEtapa, string | null>> {
  const todas = (id: string) => ({ espera: id, curso: id, interrumpida: id });
  if (e.tipo === 'salida') return todas('listo');
  if (e.tipo === 'espera-externa') return lugares.has('entrega') ? todas('listo') : todas('espera');
  if (lugares.has('recepcion')) return todas('recepcionado');
  // En la bahía de espera de control, el auto ya terminó su trabajo: celeste, como el control.
  if (lugares.has('control')) return todas('control-lavado');
  if (lugares.has('lavado')) return todas('control-lavado');
  if (lugares.has('entrega')) return todas('listo');
  if (lugares.has('bahia')) {
    if (e.categoria === 'administrativa') return todas('control-lavado');
    return { espera: 'en-cola', curso: 'en-reparacion', interrumpida: 'espera' };
  }
  return todas('recepcionado');
}

/** Definición por defecto para un proceso: los 7 estados y una asignación por etapa. */
export function semaforoPorDefecto(
  proceso: DefinicionDeProceso,
  lugares: ReadonlyMap<string, ReadonlySet<string>>,
): DefinicionDeSemaforo {
  return {
    activo: true,
    estados: SEMAFORO_POR_DEFECTO.map((e) => ({ ...e })),
    asignacion: Object.fromEntries(
      proceso.etapas.map((e) => [e.id, asignacionPorDefecto(e, lugares.get(e.id) ?? new Set())]),
    ),
  };
}

/**
 * Estado para una fase de una etapa: el de la definición o, si la etapa no está asignada (por
 * ejemplo, una etapa nueva), el que daría la regla por defecto, si ese estado existe.
 */
export function estadoDeFase(
  def: DefinicionDeSemaforo,
  etapa: Etapa,
  lugares: ReadonlySet<string>,
  fase: FaseDeEtapa,
): string | null {
  const asignada = def.asignacion[etapa.id];
  if (asignada && fase in asignada) return asignada[fase] ?? null;
  const propuesto = asignacionPorDefecto(etapa, lugares)[fase] ?? null;
  return propuesto && def.estados.some((s) => s.id === propuesto) ? propuesto : null;
}
